import { ChangeDetectionStrategy, ChangeDetectorRef, Component, HostBinding, Input } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { AccountBalanceService } from '@frontend/sports/account-balance/feature';
import { BetslipConfig, EpcotConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { exhaustiveCheckGuard, isDefined } from '@frontend/sports/common/core/utils/extended-types';
import { BetslipDisplayMode } from '@frontend/sports/types/components/bet-placement';
import { OddsDisplayFormat } from '@frontend/sports/types/models/user-settings';
import { UserService, UserStateService } from '@frontend/sports/user/feature';
import { CashierService } from '@frontend/vanilla/core';
import { Store, createSelector } from '@ngrx/store';
import { Observable, filter, map, share } from 'rxjs';

import { ArcRestrictionService } from '../../../../lib/arc/arc-restriction.service';
import { FeatureLocation, IARCProfileConfiguration } from '../../../../lib/arc/arc.model';
import { MarketSubTypeLogoShowStrategy, MarketSubTypeLogoStrategy } from '../../../../lib/marketsubtype-logo/marketsubtype-logo-display.strategy';
import { IUiRootState, uiStateSelector } from '../../../../lib/ui-manager/ui-manager.state';
import { UiStates } from '../../../../lib/ui-manager/ui-states.enum';
import BetslipActions from '../../../base/store/actions';
import { betslipBaseSelector } from '../../../base/store/selectors';
import { IBetslipRootState } from '../../../base/store/state';
import { BetslipState } from '../../../core/betslip-state';
import { OverAskFlowState } from '../../../model/over-ask/over-ask';
import { overAskFlowStateSelector } from '../../../modules/over-ask/selectors';
import { betslipPicksListCountSelector } from '../../../modules/picks/selectors';
import { betslipTypesBaseSelector } from '../../../modules/types/base/selectors';

enum BetslipStage {
    Empty,
    Edit,
    Result,
}

export const betslipComponentSelector = createSelector(
    betslipBaseSelector,
    betslipTypesBaseSelector,
    betslipPicksListCountSelector,
    (baseState, baseTypes, picksCount) => ({
        state: baseState.state,
        displayMode: baseState.displayMode,
        selectedType: baseTypes.currentSelectedType,
        picksCount,
    }),
);

@Component({
    selector: 'bs-betslip',
    templateUrl: './betslip.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    styleUrls: ['./betslip.component.scss'],
    providers: [{ provide: MarketSubTypeLogoStrategy, useClass: MarketSubTypeLogoShowStrategy }],
    standalone: false,
})
export class BetslipComponent {
    @HostBinding('class') className = 'betslip betslip-vars-scope';

    @HostBinding('class.in-column')
    get inColumnClass() {
        return this.inColumn;
    }

    @HostBinding('class.over-ask--opened') isOveraskOpened = false;

    @Input() inColumn: boolean;

    Linear = BetslipDisplayMode.Linear;
    Tabbed = BetslipDisplayMode.Tabbed;
    BetslipStage = BetslipStage;
    isUserOnPlayBreak: boolean;
    oddsDisplayFormat$: Observable<OddsDisplayFormat>;
    betslipState$: Observable<{ stage: BetslipStage; displayMode: BetslipDisplayMode }>;
    profileDetails$: Observable<IARCProfileConfiguration>;
    hasArcPreBetMessage$: Observable<boolean>;
    featureLocation = FeatureLocation;
    showBalanceAndDeposit: boolean;
    private bonusBalance: number;
    private sportsBalance: number;
    accountBalance: number;

    constructor(
        private store: Store<IBetslipRootState & IUiRootState>,
        private arcRestrictionService: ArcRestrictionService,
        userState: UserStateService,
        changeDetector: ChangeDetectorRef,
        public betslipConfig: BetslipConfig,
        public siteCore: Sitecore,
        private userService: UserService,
        private epcotConfig: EpcotConfig,
        private accountBalanceService: AccountBalanceService,
        private cashierService: CashierService,
    ) {
        this.oddsDisplayFormat$ = userState.userState$().pipe(
            map((state) => state.oddsFormat || OddsDisplayFormat.EU),
            share(),
        );

        this.store
            .select(uiStateSelector)
            .pipe(
                map((state) => state.states.some((s) => s === UiStates.RightColumnVisible)),
                takeUntilDestroyed(),
            )
            .subscribe((inColumn) => {
                this.inColumn = inColumn;
                changeDetector.markForCheck();
            });
        this.arcRestrictionService.isUserOnPlayBreak$.pipe(takeUntilDestroyed()).subscribe((data) => {
            this.isUserOnPlayBreak = data;
        });
        this.profileDetails$ = this.arcRestrictionService.profileDetails;
        this.hasArcPreBetMessage$ = this.arcRestrictionService.preBetslipMessageEnabled$;
        this.accountBalanceService.availableBalance?.pipe(filter(isDefined), takeUntilDestroyed())?.subscribe((x) => {
            this.sportsBalance = x?.availableBalance || 0;
            this.accountBalance = this.sportsBalance + this.bonusBalance;
            changeDetector.markForCheck();
        });

        this.accountBalanceService.currentBonusBalanceObservable?.pipe(filter(isDefined), takeUntilDestroyed())?.subscribe((bonus) => {
            this.bonusBalance = bonus?.BETTING || bonus?.SPORTSBOOK || 0;
            this.accountBalance = this.sportsBalance + this.bonusBalance;
            changeDetector.markForCheck();
        });
        this.showBalanceAndDeposit = this.showBalanceAndDepositOnBetslip();

        this.betslipState$ = this.store.select(betslipComponentSelector).pipe(
            map((componentState) => ({
                stage: this.getBetslipStage(componentState),
                displayMode: componentState.displayMode,
            })),
        );
        this.store
            .select(overAskFlowStateSelector)
            .pipe(takeUntilDestroyed())
            .subscribe((flowState) => {
                this.isOveraskOpened = flowState !== OverAskFlowState.None && this.betslipConfig.isBalanceHeaderEnabled;
                changeDetector.markForCheck();
            });
    }

    private showBalanceAndDepositOnBetslip(): boolean {
        return (
            this.userService.isAuthenticated &&
            this.betslipConfig.showBalanceAndDepositOnBetslip &&
            this.epcotConfig.isEnabled &&
            !this.betslipConfig.isBalanceHeaderEnabled
        );
    }

    setDisplayMode(mode: BetslipDisplayMode): void {
        this.store.dispatch(BetslipActions.toggleBetslipDisplayMode({ mode }));
    }

    get showDisplayModeSwitcher(): boolean {
        return this.betslipConfig.displayMode === BetslipDisplayMode.Linear && this.betslipConfig.isLinearToggleSwitcherEnabled;
    }

    goToDeposit(): void {
        this.cashierService.goToCashierDeposit({});
    }

    private getBetslipStage(componentState: ReturnType<typeof betslipComponentSelector>): BetslipStage {
        switch (componentState.state) {
            case BetslipState.Edit: {
                if (componentState.selectedType === null || componentState.picksCount === 0) {
                    return BetslipStage.Empty;
                }

                return BetslipStage.Edit;
            }
            case BetslipState.Placing:
                return BetslipStage.Edit;
            case BetslipState.Result:
                return BetslipStage.Result;
            default:
                return exhaustiveCheckGuard(componentState.state);
        }
    }
}
